/* eslint-disable @typescript-eslint/no-explicit-any */
import {Catalog} from 'models/catalog';
import {FC, useContext} from 'react';
import {
  GET_CATALOGS,
  GET_MONETIZATION_CONFIGURATIONS,
} from 'graphql/absences/Querys';
import {authContext} from 'contextApi/context/auth';
import {formatDate} from 'commons/dates';
import {useNavigate} from 'react-router-dom';
import {LeavesRoutes} from 'navigation/routes';
import {useLogger} from 'hooks/useLogger';
import {alertContext} from 'contextApi/context/error';
import {isBrowserFile} from 'commons/files';
import useAttachFile, {FileWrapper} from 'hooks/useAttachFile';
import GqlQueryController from 'controllers/GqlQueryController/GqlQueryController';
import {END_AT_MORNING, START_AT_EVENING} from 'models/halfDay';

interface GraphqlError {
  message: string;
  code: string;
  extensions?: GraphqlErrorExtension;
}

interface GraphqlErrorExtension {
  code: string;
  message: string;
  serviceNames: string;
}

interface GraphqlErrors {
  graphQLErrors?: GraphqlError[];
  networkError?: Error | string;
  message?: string;
  name?: string;
}

function isGraphqlError(obj: any): obj is GraphqlErrors {
  return obj && obj.graphQLErrors;
}

export const buildLeaveRequestVariables = (
  formData: any,
  userId: string | undefined,
  {
    isDraft,
    useSubLeaveType,
  }: {
    isDraft?: boolean;
    useSubLeaveType?: boolean;
  } = {}
) => {
  const scheduleOptions: string[] = [];
  formData.leaveRangeDate.startCheck && scheduleOptions.push(START_AT_EVENING);
  formData.leaveRangeDate.endCheck && scheduleOptions.push(END_AT_MORNING);

  return {
    leaveRequest: {
      userId,
      startDate: formatDate(formData.leaveRangeDate.startDate),
      endDate: formatDate(formData.leaveRangeDate.endDate),
      typeOfLeaveId: Number(
        useSubLeaveType ? formData.subLeaveTypeId : formData.leaveTypeId
      ),
      isDraft: !!isDraft,
      scheduleOptions,
    },
  };
};

export const withFormViewModel = () => (Component: FC<any>) => (props: any) => {
  const log = useLogger(withFormViewModel.name);
  const {uploadFiles} = useAttachFile();
  const {userDetails} = useContext(authContext);
  const navigate = useNavigate();
  const {dispatch} = useContext(alertContext);

  const getFiles = (formData: any): FileWrapper[] => {
    return Object.entries(formData)
      .filter(([, value]) => isBrowserFile(value))
      .map(([key, value]) => ({
        file: value,
        evidenceType: key,
      })) as unknown as FileWrapper[];
  };

  const variablesMapper = (formData: any, options: any = {}) =>
    buildLeaveRequestVariables(formData, userDetails?.data?.id, options);

  const submit = (mutationPromise: any, data: any) => {
    const files: FileWrapper[] = getFiles(data.formData);
    return new Promise((resolve, reject) => {
      mutationPromise
        .then((response: any) => {
          const leaveRequest =
            response.data.createLeaveRequest ||
            response.data.updateLeaveRequest ||
            response.data.updateLeaveRequestDraft ||
            response.data.updateLeaveRequestStatus;
          uploadFiles(files, leaveRequest.id, onComplete, handleError)
            .then(() => {
              console.log('successsss');
              resolve(response);
            })
            .catch(reject);
        })
        .catch(handleError);
    });
  };

  const handleError = (error: any) => {
    if (isGraphqlError(error)) {
      const messages = error.graphQLErrors
        ?.map(err => {
          log.debug('message', err);
          return err.extensions?.message || err.message;
        })
        ?.join('\n');
      log.error('Graphql error', messages);
      dispatch({
        type: 'show',
        payload: {
          message: messages || 'Algo salió mal',
          level: 'error',
        },
      });
    } else {
      log.error('Generic error', error);
    }
  };

  const onComplete = (requestId: string) => {
    log.debug('Request created', requestId);
    dispatch({
      type: 'show',
      payload: {
        level: 'success',
        message: 'Solicitud creada con éxito',
        duration: 'short',
        callback: () => {
          log.debug('on callback');
          navigate(`../${LeavesRoutes.REQUEST_DETAIL}/${requestId}`);
        },
      },
    });
  };

  return (
    <GqlQueryController
      QUERY={GET_CATALOGS}
      variables={{
        type: 'LEAVE_TYPE',
        country: userDetails?.data?.country,
      }}
      initialState={[] as Catalog[]}
      rootField="catalogs"
    >
      {({data: leaveTypes}) => (
        <GqlQueryController
          QUERY={GET_MONETIZATION_CONFIGURATIONS}
          variables={{
            country: userDetails?.data?.country,
            status: 'ACTIVE',
          }}
          initialState={{data: []}}
          rootField="getMonetizationConfigurations"
        >
          {({data: monetizationConfig}) => {
            // Extraer el leaveType único de las configuraciones de monetización
            const monetizationLeaveTypes: Catalog[] = (
              monetizationConfig?.data || []
            )
              .map((config: any) => config.leaveType)
              .filter(Boolean)
              .filter(
                (lt: any, index: number, self: any[]) =>
                  index === self.findIndex((t: any) => t.id === lt.id)
              );

            // Inyectar los leave types de monetización que no estén ya en el catálogo
            const existingIds = new Set(leaveTypes.map((lt: Catalog) => lt.id));
            const mergedLeaveTypes: Catalog[] = [
              ...leaveTypes,
              ...monetizationLeaveTypes.filter(
                (lt: any) => !existingIds.has(lt.id)
              ),
            ];

            return (
              <Component
                leaveTypes={mergedLeaveTypes}
                variablesMapper={variablesMapper}
                onSubmit={submit}
                {...props}
              />
            );
          }}
        </GqlQueryController>
      )}
    </GqlQueryController>
  );
};

export default withFormViewModel;
